import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/browser';

interface CommunityStatistics {
  trending: {
    topics: number;
    topTags: Array<{ tag: string; count: number }>;
  };
  active: {
    users: number;
    formatted: string;
  };
  recent: {
    posts: number;
  };
}

const DEFAULT: CommunityStatistics = {
  trending: { topics: 0, topTags: [] },
  active: { users: 0, formatted: '0' },
  recent: { posts: 0 },
};

export function useCommunityStatistics() {
  const [statistics, setStatistics] = useState<CommunityStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = useCallback(async () => {
    const supabase = createClient();
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const fifteenMinAgo = new Date(now.getTime() - 15 * 60 * 1000).toISOString();

    try {
      // 1. Posts created today
      const { count: recentPosts } = await supabase
        .from('community_posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', todayStart);

      // 2. Trending tags from last 24 hours
      const { data: taggedPosts } = await supabase
        .from('community_posts')
        .select('tags')
        .gte('created_at', oneDayAgo)
        .not('tags', 'is', null);

      const tagCount: Record<string, number> = {};
      for (const post of taggedPosts ?? []) {
        for (const tag of (post.tags as string[] ?? [])) {
          tagCount[tag] = (tagCount[tag] ?? 0) + 1;
        }
      }
      const topTags = Object.entries(tagCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([tag, count]) => ({ tag, count }));

      // 3. Active users — count unique users who posted/liked/commented recently
      // (profiles.last_active column does not exist; use activity tables as proxy)
      let activeUserCount = 0;

      const [{ data: postersData }, { data: likersData }, { data: commentersData }] =
        await Promise.all([
          supabase.from('community_posts').select('user_id').gte('created_at', fifteenMinAgo),
          supabase.from('post_likes').select('user_id').gte('created_at', fifteenMinAgo),
          supabase.from('post_comments').select('user_id').gte('created_at', fifteenMinAgo),
        ]);

      const activeSet = new Set([
        ...(postersData ?? []).map((r: any) => r.user_id),
        ...(likersData ?? []).map((r: any) => r.user_id),
        ...(commentersData ?? []).map((r: any) => r.user_id),
      ]);
      activeUserCount = activeSet.size;

      const formatted =
        activeUserCount >= 1000
          ? `${(activeUserCount / 1000).toFixed(1)}k`
          : String(activeUserCount);

      setStatistics({
        trending: { topics: topTags.length, topTags },
        active: { users: activeUserCount, formatted },
        recent: { posts: recentPosts ?? 0 },
      });
      setError(null);
    } catch (err) {
      console.error('[useCommunityStatistics]', err);
      setError('Failed to load statistics');
      setStatistics(DEFAULT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();

    const interval = setInterval(fetchStatistics, 60_000);

    const supabase = createClient();
    const channel = supabase
      .channel('community-statistics-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'community_posts' },
        fetchStatistics
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [fetchStatistics]);

  return { statistics, loading, error, refetch: fetchStatistics };
}
